import "./webpack-set-public-path";
import { datadogRum } from "@datadog/browser-rum";

import initSentry from "app/sentry";

import { bootstrap } from "./bootstrap";
import type { BootstrapPhase } from "./bootstrap-timing";
import { endBootstrapPhase, startBootstrapPhase } from "./bootstrap-timing";
import { reportFatalOnce } from "./bootstrap-utils";
import { importWithRetry, reloadOnceOnChunkLoadFailure } from "./import-with-retry";

import { registerApplication, start } from "single-spa";

import "./dayjs-extend";
import Sentry from "@similarweb/sentry-logger";

import initDatadogRum from "app/datadogRum";

$(document).on("MixpanelReady", () => {
    const urlParams = new URLSearchParams(window.location.search);
    const campaignId = urlParams?.get("campaignId");
    const trimCampaignId = campaignId?.replace("/", "");
    if (trimCampaignId && window.mixpanel) {
        // register several super properties when a user click on a link from email
        window.mixpanel.register({
            campaignId: trimCampaignId,
        });
        // Delete the campaignId parameter.
        urlParams.delete("campaignId");
        const hashUrl = `${location.hash}`;
        const newUrl = `${location.pathname}${hashUrl}`;

        // rewrite the url. clean up our mess.
        window.history.replaceState({}, "", newUrl);
    }
});

const matchesRegex = (regex: RegExp) => {
    return (location: Location) => {
        return regex.test(location.hash);
    };
};

const loadChunkWithRetry = <T>(phase: BootstrapPhase, importChunk: () => Promise<T>) => {
    startBootstrapPhase(phase);
    return importWithRetry(importChunk)
        .catch((error) => {
            reloadOnceOnChunkLoadFailure(error);
            throw error;
        })
        .finally(() => {
            endBootstrapPhase(phase);
        });
};

const loadReactApp = () =>
    loadChunkWithRetry("react-chunk", () =>
        import(/* webpackChunkName: "react", webpackPrefetch: true */ "./react-app/react-app.app"),
    );

const loadLegacyReactApp = () =>
    loadChunkWithRetry("react-chunk-legacy", () =>
        import(/* webpackChunkName: "react", webpackPrefetch: true */ "./react/react.app"),
    );

const run = async (): Promise<void> => {
    try {
        initSentry();
        const rumWasInitialized = initDatadogRum();
        if (rumWasInitialized) {
            datadogRum.startView({
                name: "bootstrap",
            });
        }

        const commonEnv = await bootstrap();

        registerApplication("react-app", loadReactApp, matchesRegex(/^(?!.*(#\/ripng|#\/sspa\/))/), commonEnv);
        registerApplication("react", loadLegacyReactApp, matchesRegex(/^#\/sspa\//), commonEnv);

        start();
    } catch (e) {
        if (Sentry.isInitialized()) {
            reportFatalOnce(e, "PRO encountered a fatal error while trying to initiate the app in spa-config");
        } else {
            // Need to implement a custom error handling mechanism here to alert on call (Slack?)
            // Maybe also to add a custom reporting endpoint in case Sentry failed to initialize
            console.error("SW initiation crashed with a fatal error", e);
        }
    }
};

void run();
